import type { AppointmentLocale } from "@/locales/fr/appointment";

import { EAppointmentCancellationReasonType } from "@/types/backoffice/dto/appointment/appointment-cancellation-reason-types";
import { EAppointmentState } from "@/types/backoffice/dto/appointment/appointment-state-dto.ts";
import { EAppointmentType } from "@/types/backoffice/dto/appointment/appointment-type-dto.ts";

const appointmentLocale: AppointmentLocale = {
  appointment: {
    editAppointment: "Update appointment",
    manual: {
      bookManualAppointment: "Book manual appointment",
      date: "Appointment date",
      startTime: "Start time",
      endTime: "End time",
      comment: "Comment",
      minDuration:
        "Minimum intervention slot duration of {totalDurationInHour} minutes, including {duration} minutes (intervention duration) + 15 minutes of slot buffer",
      durationWarning:
        "The duration between the start and the end is shorter than the minimum required duration ({totalDurationInMinutes} minutes), i.e. {totalDurationInHour} minutes",
      isForcedAppointmentQuestion:
        "Do you want to generate the appointment in the scheduling tool with {serviceProviderName} ?",
    },
    updateSitePartnerAddress: "Update site geolocation",
    noPhone: {
      label: "Subscriber has no phone number, this is mandatory in order to book an appointment",
      cta: "Update subscriber",
    },
    noMail: {
      label: "Subscriber has no email, this is mandatory in order to book an appointment",
      cta: "Update subscriber",
    },
    partnerAddressModal: {
      header: "Site Address Confirmation",
      addressNotFound:
        "We were unable to find the provided address. Here are several suggestions corresponding to your input to estimate the technician's travel time to the appointment location",
      registeredAddress: "Registered site address",
      addressSuggestions: "Address Suggestions",
    },
    bookedAppointmentIsCompromised:
      "<span style='font-weight: bolder;'>Appointment compromised</span>, please reschedule with subscriber",
    hasAlreadyBookedAppointment:
      "An appointment is already booked. Please cancel or edit the first one before booking another one",
    cancelModal: {
      header: "Appointment cancelling confirmation",
      cancellingAppointment:
        "You are about to cancel the appointment scheduled for {startDate} between {startTime} and {endDate}",
      editWithPencil:
        "If you simply want to modify the date, please use the modification tool represented by a pencil.",
      whatNext: "What action should we give to this cancellation ?",
      reason: "What is the reason for this cancellation ?",
      cancel: "Cancel the appointment",
      chooseReason: "Choose a reason",
      cancellationReason: {
        [EAppointmentCancellationReasonType.DEFINITIVE]: "Final cancellation (Subscriber wont be contacted anymore)",
        [EAppointmentCancellationReasonType.TEMP_SOON]:
          "Temporary cancellation, to be recontacted as soon as possible (Subscriber will be contacted tomorrow)",
        [EAppointmentCancellationReasonType.TEMP_MONTH]:
          "Temporary cancellation, to be recontacted in 15 days (Subscriber will be contacted within 15 days)",
      },
    },
    title: "Appointments",
    bookAppointment: "Book an appointment",
    history: {
      title: "History",
    },
    confirmedAppointment: "Appointment confirmed on",
    states: {
      [EAppointmentState.CANCELED]: "Canceled",
      [EAppointmentState.PLANNED]: "Planned",
      [EAppointmentState.FINALIZED]: "Finalized",
      compromised: "Compromis",
    },
    types: {
      [EAppointmentType.TECHNICAL_MAINTENANCE]: "Technical Maintenance",
      [EAppointmentType.INSTALLATION]: "V5 Installation",
      [EAppointmentType.COMPLEX_INSTALLATION]: "V5 Complex Installation",
      [EAppointmentType.UNINSTALL]: "Uninstallation",
      [EAppointmentType.ADMINISTRATIVE]: "Administrative Update",
      [EAppointmentType.INSTALLATION_VX]: "Vx Installation",
      [EAppointmentType.QUALITY_CONTROL]: "Quality Control",
      [EAppointmentType.COMPLIANCE_CONTROL]: "Compliance Service Call",
      [EAppointmentType.UPMARKET]: "Upmarket",
      [EAppointmentType.INSTALLATION_FINALIZATION]: "Installation Finalization",
      [EAppointmentType.PAC_INSTALLATION]: "Heat Pump Installation",
      [EAppointmentType.PAC_REINTERVENTION]: "Heat Pump Reintervention",
      [EAppointmentType.PAC_MAINTENANCE]: "Heat Pump Technical Maintenance",
      [EAppointmentType.PAC_UPMARKET]: "Heat Pump Upmarket",
      [EAppointmentType.PAC_FINALIZATION]: "Heat Pump Installation Finalization",
      [EAppointmentType.PAC_UNINSTALL]: "Heat Pump Uninstallation",
    },
    bookModal: {
      header: "Booking slots",
      availabilities: {
        searchInProgress: "Search in progress...",
        searchInProgressLabel: "Please wait, search may take several seconds",
        error: "An error occurred",
        errorLabel: "Please try again later",
        nextMonth: "Next Month",
        previousMonth: "Previous Month",
      },
      confirmation: {
        flex: "Flex",
        partner: "Partner",
        type: "Type",
        title: "Slot confirmation",
        verifyInfos: "Please check infos about the appointment slot",
        date: "Date",
        slot: "Slots",
        provider: "Service Provider",
        bookAppointment: "Book Appointment",
        tooCloseInTimeWarning:
          "Please note that appointments scheduled less than 3 hours in advance are only allowed for planning services.",
      },
      bookSlot: {
        confirmInProgress: "Appointment confirmation in progress...",
        confirmInProgressLabel: "Please wait, confirmation may take several seconds...",
        error: "An error occurred",
        errorLabel: "Please try again later",
        booked: "Appointment booked",
        bookedLabel: "Appointment has been booked, you can close the window",
      },
    },
    bookingForm: {
      updateReason: "Update reason",
      eligibleApplianceNumber: "Radiators and towel dryers number",
      totalEligibleApplianceNumber: "Eligible Appliance Number",
      appointmentType: "Appointment Type",
      serviceProvider: "Service Provider",
      month: "Month",
      search: "Search slots",
      searchType: "Selection and choice of time slot",
      searchTypeManual: "Manual",
      searchTypeAutomatic: "Automatic search",
    },
    noPdlContract: {
      header: "The electricity contract photo is missing",
      pdlMissingAndElectricityContractMissing:
        "The photo of the member’s electricity bill or proof of residence from their energy provider is missing. You must request this document from the member, who will either need to send it back by email or have it ready for the technician’s visit.",
      doNotSendMail: "Do not send email",
      sendMail: "Send email",
      warningSendingMail:
        "Subscriber has already been notified in a previous mailing on {lastSendingDate}. Resend only if necessary.",
    },
    contextual: {
      update: "Update",
      cancel: "Cancel",
    },
    slots: "Slots",
    appointmentBooking: "Appointment booking",
    confirmation: "Confirmation",
    flex: {
      flexAvailable: "An independent installer is available in this department.",
      bookFlexAppointment: "Book a flex appointment",
    },
    appointmentBlocked:
      "Appointment not available. The type of accommodation is unknown. Please enter the details to make an appointment.",
    components: {
      calendar: {
        selected: "Selected",
        errorLoadingAvailabilities: "An error occurred while loading availabilities.",
        noAvailabilitiesThisDay: "No availability this day",
        previous: "Previous",
        next: "Next",
        selectedSlot: "Selected slot:",
        confirmAppointment: "Confirm appointment",
        availableProviders: "Available providers",
        providerFallback: "Provider",
        flexibleAppointmentsAvailable: "An independent installer is available in this department.",
        chooseFlexibleAppointment: "Book a flex appointment",
        filteringByPreferredProvider: "Slots filtered for preferred provider: {provider}",
        showingAllProviders: "Showing all available providers",
        preferredProviderDescription: "You are viewing only this provider's slots",
        allProvidersDescription: "You are viewing all providers' slots",
        showAllProviders: "Show all providers",
        showPreferredProvider: "Back to preferred provider",
        daysShortByIndex: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        monthsShortByIndex: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        weekLabel: {
          current: "This week",
          next: "Next",
          weekPlus: "Week +{n}",
        },
        availabilityLabels: {
          loading: "Loading…",
          high: "Available",
          medium: "Few availabilities",
          low: "Last slots",
          none: "No availability",
        },
      },
    },
  },
};

export default appointmentLocale;
